import React, { useCallback, useMemo } from 'react';
import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	ModalCloseButton,
	FormLabel,
	Input,
	Select,
	Flex,
	Button,
	Text,
	Box,
	FormHelperText,
	FormControl,
} from '@chakra-ui/react';
import { Select as ChakraSelect } from 'chakra-react-select';
import Locale from '../../utils/translations';
import { Template } from '../../utils/interfaces';

interface AddTagsModalProps {
	isOpen: boolean;
	onClose: () => void;
	templateName: string;
	setTemplateName: (val: string) => void;
	templateData: { [key: string]: string };
	setTemplateData: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
	TEMPLATE_FIELDS: {
		key: string;
		label: string;
		isRequired: boolean;
		isDisabled: boolean;
		defaultValue?: string;
		disclaimer?: string;
	}[];
	samples: { sampleId: string; name: string }[];
	selectedSampleId: string;
	setSelectedSampleId: (val: string) => void;
	isCreating: boolean;
	error: string;
	setError: (msg: string) => void;
	onAddTemplate: () => void;
	section: 'tests' | 'templates' | 'translation';
	selectedTemplateId?: string[]; // <-- change to array
	setSelectedTemplateId?: (val: string[]) => void; // <-- change to array
	templates?: Template[];
	isDuplicating?: boolean;
}

const AddTagsModal: React.FC<AddTagsModalProps> = ({
	isOpen,
	onClose,
	templateName,
	setTemplateName,
	templateData,
	setTemplateData,
	TEMPLATE_FIELDS,
	samples,
	selectedSampleId,
	setSelectedSampleId,
	isCreating,
	error,
	setError,
	onAddTemplate,
	section,
	selectedTemplateId = [],
	setSelectedTemplateId = () => {},
	templates = [],
}) => {
	// State for new tag key input not neede for now
	// const [newTagKey, setNewTagKey] = useState('');
	// useEffect(() => {
	// 	if (!isOpen) {
	// 		setNewTagKey('');
	// 	}
	// }, [isOpen]);
	const handleChange = useCallback(
		(key: string, value: string) => {
			setTemplateData(prev => ({ ...prev, [key]: value }));
		},
		[setTemplateData],
	);

	// Addition of new tags not needed for now
	// const handleAddNewTag = useCallback(() => {
	// 	if (newTagKey && !templateData[newTagKey]) {
	// 		setTemplateData(prev => ({ ...prev, [newTagKey]: '' }));
	// 		setNewTagKey('');
	// 	}
	// }, [newTagKey, templateData, setTemplateData]);

	const templateOptions = useMemo(
		() =>
			templates.map(t => ({
				value: t.templateId,
				label: t.name,
			})),
		[templates],
	);

	const handleAdd = () => {
		const missingField = TEMPLATE_FIELDS.find(
			field => field.isRequired && !templateData[field.key]?.trim(),
		);
		if (missingField) {
			setError(`${Locale.get(missingField.label)} is required.`);
			return;
		}
		setError('');
		onAddTemplate();
	};

	const selectedTemplateValue = useMemo(
		() =>
			templates
				.filter(t => selectedTemplateId.includes(t.templateId))
				.map(t => ({ value: t.templateId, label: t.name })),
		[templates, selectedTemplateId],
	);

	const extraTags = useMemo(
		() => Object.keys(templateData).filter(key => !TEMPLATE_FIELDS.some(f => f.key === key)),
		[templateData, TEMPLATE_FIELDS],
	);

	return (
		<Modal isOpen={isOpen} onClose={onClose} isCentered>
			<ModalOverlay />
			<ModalContent maxW="530px" w="90%">
				<ModalHeader>
					<Text fontWeight="bold">
						{section === 'templates'
							? Locale.get('Add template')
							: Locale.get('Create a test run')}
					</Text>
				</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					{section === 'templates' ? (
						<>
							<FormLabel>{Locale.get('Template Name')}</FormLabel>
							<Input
								bg={'white'}
								mb={3}
								placeholder="Template Name"
								value={templateName}
								onChange={e => setTemplateName(e.target.value)}
							/>
						</>
					) : (
						<>
							<Box mb={5}>
								<FormLabel>{Locale.get('Test Name')}</FormLabel>
								<Input
									bg={'white'}
									placeholder="Test Name"
									value={templateName}
									onChange={e => setTemplateName(e.target.value)}
								/>
							</Box>
							<Box mb={5}>
								<FormLabel>{Locale.get('Select Templates')}</FormLabel>
								<ChakraSelect
									isMulti
									options={templateOptions}
									chakraStyles={{
										control: provided => ({
											...provided,
											_light: { backgroundColor: 'white' },
											_dark: { backgroundColor: 'black' },
											shadow: '0px 4px 4px 0px rgba(74, 74, 74, 0.04)',
										}),
										dropdownIndicator: provided => ({
											...provided,
											_light: { backgroundColor: 'white' },
											_dark: { backgroundColor: 'black' },
										}),
									}}
									placeholder="Select templates"
									value={selectedTemplateValue}
									closeMenuOnSelect
									maxMenuHeight={150}
									onChange={vals => {
										const ids = Array.isArray(vals)
											? vals.map(v => v.value)
											: [];
										setSelectedTemplateId(ids);
									}}
								/>
							</Box>
						</>
					)}

					{section === 'templates' && (
						<>
							{TEMPLATE_FIELDS.map(field => (
								<FormControl key={field.key} isRequired={field.isRequired} mb={3}>
									<FormLabel>{Locale.get(field.label)}</FormLabel>
									<Input
										placeholder={field.label}
										value={templateData[field.key] || field.defaultValue || ''}
										onChange={e => handleChange(field.key, e.target.value)}
										bg={'white'}
										isDisabled={field.isDisabled}
										isRequired={field.isRequired}
									/>
									{field.disclaimer && (
										<FormHelperText fontSize="xs">
											{Locale.get(field.disclaimer)}
										</FormHelperText>
									)}
								</FormControl>
							))}

							{extraTags.map(key => (
								<React.Fragment key={key}>
									<FormLabel>{key}</FormLabel>
									<Input
										mb={3}
										placeholder={key}
										value={templateData[key]}
										onChange={e =>
											setTemplateData(prev => ({
												...prev,
												[key]: e.target.value,
											}))
										}
									/>
								</React.Fragment>
							))}
							{/* addition of tags not neede for now */}
							{/* <Flex mt={2} gap={2}>
								<Input
									placeholder="New tag key"
									size="sm"
									value={newTagKey}
									onChange={e => setNewTagKey(e.target.value)}
								/>
								<Button size="sm" colorScheme="blue" onClick={handleAddNewTag}>
									Add
								</Button>
							</Flex> */}
						</>
					)}
					{section === 'tests' && (
						<Box mb={3}>
							<FormLabel>{Locale.get('Samples')}</FormLabel>
							<Select
								mb={2}
								bg={'white'}
								placeholder="Select a sample"
								value={selectedSampleId}
								onChange={e => setSelectedSampleId(e.target.value)}
							>
								{samples.map(sample => (
									<option key={sample.sampleId} value={sample.sampleId}>
										{sample.name}
									</option>
								))}
							</Select>
						</Box>
					)}

					{error && (
						<Text color="red" fontSize="14px" mb={2}>
							{error}
						</Text>
					)}
				</ModalBody>
				<ModalFooter>
					<Flex gap={2} w="100%">
						<Flex gap={2} w="100%">
							<Button
								variant="bePrimary"
								onClick={handleAdd}
								isLoading={isCreating}
								disabled={isCreating}
								w="50%"
							>
								{Locale.get('Add')}
							</Button>
							<Button onClick={onClose} disabled={isCreating} w="50%">
								{Locale.get('Cancel')}
							</Button>
						</Flex>
					</Flex>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

export default AddTagsModal;
